@if (!editMode) {
<div class="info-section-card card-indigo-liseret" (click)="$event.stopPropagation()">
  <!-- Header / Action Summary -->
  <div
    class="flex flex-row items-center justify-between w-full gap-4 pb-5 border-b border-solid border-slate-150 dark:border-slate-800/80 mb-6">
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 mb-1.5 flex-wrap">
        <span class="text-xs font-bold uppercase tracking-widest reference-label">{{ 'ORDERS.TRANSACTION_ID' |
          translate }}</span>
        <span class="px-2.5 py-0.5 rounded-full text-xs font-mono font-bold reference-badge">
          {{ order.transactionId ? order.transactionId : '--' }}
        </span>
      </div>
      <div class="flex items-center gap-2 mb-1.5 flex-wrap">
        <span class="text-xs font-bold uppercase tracking-widest reference-label">{{ 'reference' |
          translate }}</span>
        <span class="px-2.5 py-0.5 rounded-full text-xs font-mono font-bold reference-badge">
          {{ order.reference ? order.reference : '--' }}
        </span>
      </div>
      <h1
        class="text-base md:text-lg font-bold tracking-tight leading-tight m-0 text-ellipsis overflow-hidden text-slate-900 dark:text-white">
        {{ order.name ? order.name : '--' }}
      </h1>
    </div>

    <div class="flex items-center gap-1 shrink-0">
      @if (authService.hasRole([RolesEnum.ORDER_WRITE, RolesEnum.SALES])) {
      <button mat-icon-button (click)="editMode = true" [matTooltip]="'ORDERS.MODIFY' | translate">
        <mat-icon>edit</mat-icon>
      </button>
      }

      @if (dialogRef) {
      <button mat-icon-button (click)="dialogRef.close()" [matTooltip]="'common.close' | translate">
        <mat-icon>close</mat-icon>
      </button>
      }
    </div>
  </div>

  <div class="flex flex-col gap-8">
    <!-- General Details Section -->
    <div>
      <div class="section-header">
        <div class="section-badge-icon badge-indigo-icon">
          <mat-icon>analytics</mat-icon>
        </div>
        <h2>{{ 'common.information.title' | translate }}</h2>
      </div>

      <div class="info-grid">
        <!-- Order Date -->
        <div class="info-tile">
          <div class="tile-badge badge-blue">
            <mat-icon>calendar_today</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'account.dateTitle.order' | translate }}</span>
            <span class="tile-value">{{ order.date | date:'shortDate' : '' : translate.currentLang }}</span>
          </div>
        </div>

        <!-- Delivery Date -->
        <div class="info-tile">
          <div class="tile-badge badge-amber">
            <mat-icon>local_shipping</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'ORDERS.DELIVERY_DATE' | translate }}</span>
            <span class="tile-value">{{ order.startDeliveryDate | date:'shortDate' : '' : translate.currentLang
              }}</span>
          </div>
        </div>

        @if (authService.hasRole([RolesEnum.ORDER_WRITE, RolesEnum.SALES])) {
        <!-- Production Plant -->
        <div class="info-tile">
          <div class="tile-badge badge-purple">
            <mat-icon>factory</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'shipment.site.label' | translate }}</span>
            <span class="tile-value">{{ order.prodPlant ? order.prodPlant : '--' }}</span>
          </div>
        </div>

        <!-- Customer -->
        <div class="info-tile">
          <div class="tile-badge badge-rose">
            <mat-icon>business</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'customer' | translate }}</span>
            <span class="tile-value">
              @if (order.company || order.contactName) {
              @if (order.company && order.contactName) {
              {{ order.company }}, {{ order.contactName }}
              } @else {
              {{ order.company || order.contactName }}
              }
              } @else {
              --
              }
            </span>
          </div>
        </div>

        <!-- Commercial -->
        <div class="info-tile">
          <div class="tile-badge badge-teal">
            <mat-icon>support_agent</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'commercial' | translate }}</span>
            <span class="tile-value">{{ order.salesName ? order.salesName : '--' }}</span>
            @if (order.salesEmail) {
            <span
              class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap"
              [matTooltip]="order.salesEmail">
              {{ order.salesEmail }}
            </span>
            }
          </div>
        </div>

        <!-- Manager -->
        <div class="info-tile">
          <div class="tile-badge badge-indigo">
            <mat-icon>badge</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'ORDERS.MANAGER' | translate }}</span>
            <span class="tile-value">{{ order.managerName ? order.managerName : '--' }}</span>
            @if (order.managerEmail) {
            <span
              class="text-xs text-slate-500 dark:text-slate-400 mt-0.5 overflow-hidden text-ellipsis whitespace-nowrap"
              [matTooltip]="order.managerEmail">
              {{ order.managerEmail }}
            </span>
            }
          </div>
        </div>

        <!-- Amount -->
        <div class="info-tile col-span-full amount-tile">
          <div class="tile-badge badge-emerald">
            <mat-icon>payments</mat-icon>
          </div>
          <div class="info-content flex flex-row items-center justify-between gap-4">
            <div class="flex flex-col">
              <span class="tile-label">{{ 'account.amountTitle.order' | translate }}</span>
              <span class="tile-value-large text-emerald-600 dark:text-emerald-400">
                {{ order.amount | currency:'EUR':'symbol': '' : translate.currentLang }}
              </span>
            </div>
            <div class="flex flex-col items-end gap-1.5">
              <div class="flex flex-col items-end">
                <span class="tile-label">{{ 'ADMIN.ORDER_ACTIVE_CHECK' | translate }}</span>
                <span class="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 status-badge"
                  [class.status-active]="order.emailIsActive"
                  [class.status-inactive]="!order.emailIsActive">
                  <span class="w-1.5 h-1.5 rounded-full status-bullet"></span>
                  {{ (order.emailIsActive ? 'common.yes.label' : 'common.no.label') | translate }}
                </span>
              </div>
              @if (order.disabled) {
              <div class="flex flex-col items-end">
                <span class="px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 status-badge status-warning"
                  [matTooltip]="'ADMIN.DISABLED_ORDER' | translate">
                  <mat-icon style="font-size: 14px; width: 14px; height: 14px; margin-right: 2px;"
                    class="text-amber-600 dark:text-amber-400">warning</mat-icon>
                  {{ 'ADMIN.DISABLED_ORDER' | translate }}
                </span>
              </div>
              }
            </div>
          </div>
        </div>

        <!-- Email list -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-purple">
            <mat-icon>alternate_email</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'ADMIN.EMAIL_LIST' | translate }}</span>
            <div class="flex flex-wrap gap-1.5 mt-1">
              @if (order.customerEmails.length) {
              @for (email of order.customerEmails; track email) {
              <span class="px-2.5 py-0.5 rounded-md text-xs font-semibold font-mono email-badge">
                {{ email }}
              </span>
              }
              } @else {
              <span class="tile-value">--</span>
              }
            </div>
          </div>
        </div>
        }
      </div>
    </div>

    <mat-divider class="opacity-40"></mat-divider>

    <!-- Production & BAT Section -->
    <div>
      <div class="section-header">
        <div class="section-badge-icon badge-emerald-icon">
          <mat-icon>settings</mat-icon>
        </div>
        <h2>{{ 'ADMIN.PRODUCTION' | translate }}</h2>
      </div>

      <!-- Files View Sub-Section -->
      <h3 class="sub-section-title">
        <mat-icon>folder</mat-icon>
        <span>{{ 'ADMIN.SHOW_FILES_VIEW' | translate }}</span>
      </h3>

      <div class="info-grid">
        <!-- Requested -->
        <div class="info-tile">
          <div class="tile-badge" [ngClass]="order.emailFile >= 1 ? 'badge-emerald' : 'badge-rose'">
            <mat-icon>{{ order.emailFile >= 1 ? 'task_alt' : 'cancel' }}</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'common.requested.label' | translate }}</span>
            <span class="tile-value">{{ (order.emailFile >= 1 ? 'common.yes.label' : 'common.no.label') | translate
              }}</span>
          </div>
        </div>

        <!-- Notifications -->
        <div class="info-tile">
          <div class="tile-badge badge-amber">
            <mat-icon>notifications</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'common.notificationsNumber.button' | translate }}</span>
            <span class="tile-value font-bold text-amber-600 dark:text-amber-400">{{ order.emailFile }}</span>
          </div>
        </div>

        <!-- Uploads Date -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-blue">
            <mat-icon>cloud_upload</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'order.fileUploadsDate.label' | translate }}</span>
            <span class="tile-value">{{ order.uploadsDate ? (order.uploadsDate | date:'short' : '' :
              translate.currentLang) : '--' }}</span>
          </div>
        </div>
      </div>

      <!-- BAT View Sub-Section -->
      <h3 class="sub-section-title mt-6">
        <mat-icon>library_add_check</mat-icon>
        <span>{{ 'ADMIN.SHOW_BAT_VIEW' | translate }}</span>
      </h3>

      <div class="info-grid">
        <!-- With Proof -->
        <div class="info-tile">
          <div class="tile-badge" [ngClass]="order.batIsActive ? 'badge-blue' : 'badge-amber'">
            <mat-icon>{{ order.batIsActive ? 'verified' : 'unpublished' }}</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'withProof' | translate }}</span>
            <span class="tile-value">{{ order.batIsActive ? ('common.yes.label' | translate) : ('common.no.label' |
              translate) }}</span>
          </div>
        </div>

        <!-- Version -->
        <div class="info-tile">
          <div class="tile-badge badge-purple">
            <mat-icon>history</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'BAT.BAT_VERSION' | translate }}</span>
            <span class="tile-value">v{{ order.batVersion + 1 }}</span>
          </div>
        </div>

        <!-- Requested -->
        <div class="info-tile">
          <div class="tile-badge" [ngClass]="order.batMailSent ? 'badge-emerald' : 'badge-rose'">
            <mat-icon>{{ order.batMailSent ? 'send' : 'mail_lock' }}</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'common.requested.label' | translate }}</span>
            <span class="tile-value">{{ (order.batMailSent ? 'common.yes.label' : 'common.no.label') | translate
              }}</span>
          </div>
        </div>

        <!-- Notifications -->
        <div class="info-tile">
          <div class="tile-badge badge-amber">
            <mat-icon>notifications_active</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'common.notificationsNumber.button' | translate }}</span>
            <span class="tile-value font-bold text-amber-600 dark:text-amber-400">{{ order.emailBat }}</span>
          </div>
        </div>

        <!-- BAT Date -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-teal">
            <mat-icon>calendar_month</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'account.batDateTitle.order' | translate }}</span>
            <span class="tile-value">{{ order.batDate ? (order.batDate | date:'short' : '' : translate.currentLang) :
              '--' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Invoicing Section -->
    @if (webOrder?.invoicing) {
    <mat-divider class="opacity-40"></mat-divider>

    <div>
      <div class="section-header">
        <div class="section-badge-icon badge-amber-icon">
          <mat-icon>receipt</mat-icon>
        </div>
        <h2>{{ 'settings.okto.invoicing.title' | translate }}</h2>
      </div>

      <div class="info-grid">
        <!-- Company & Name -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-rose">
            <mat-icon>business</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'company' | translate }}</span>
            <span class="tile-value font-bold text-base">{{ webOrder.invoicing.company ? webOrder.invoicing.company :
              '--' }}</span>
            <span class="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1 font-semibold">
              <mat-icon style="font-size: 14px; width: 14px; height: 14px;">person</mat-icon>
              {{ webOrder.invoicing.name ? webOrder.invoicing.name : '--' }}
            </span>
          </div>
        </div>

        <!-- Street address -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-indigo">
            <mat-icon>place</mat-icon>
          </div>
          <div class="info-content">
            <span class="tile-label">{{ 'shipment.shipTo.title' | translate }}</span>
            <span class="tile-value">
              {{ webOrder.invoicing.street1 ? webOrder.invoicing.street1 : '--' }}
              @if (webOrder.invoicing.street2) {
              <br><span class="text-xs text-slate-500 dark:text-slate-400">{{ webOrder.invoicing.street2 }}</span>
              }
            </span>
            <span
              class="text-xs font-bold text-slate-600 dark:text-slate-300 mt-1.5 bg-slate-100 dark:bg-slate-800/60 px-2 py-0.5 rounded w-max">
              {{ webOrder.invoicing.zip }} {{ webOrder.invoicing.city }}, {{ webOrder.invoicing.country }}
            </span>
          </div>
        </div>

        <!-- Email & Phone -->
        <div class="info-tile col-span-full">
          <div class="tile-badge badge-emerald">
            <mat-icon>contact_phone</mat-icon>
          </div>
          <div class="info-content gap-1.5">
            <span class="tile-label">{{ 'contact' | translate }}</span>
            <span class="tile-value text-sm flex items-center gap-1.5 overflow-hidden text-ellipsis whitespace-nowrap">
              <mat-icon style="font-size: 14px; width: 14px; height: 14px;" class="text-slate-400">email</mat-icon>
              {{ webOrder.invoicing.email ? webOrder.invoicing.email : '--' }}
            </span>
            <span class="tile-value text-sm flex items-center gap-1.5">
              <mat-icon style="font-size: 14px; width: 14px; height: 14px;" class="text-slate-400">phone</mat-icon>
              {{ webOrder.invoicing.phone ? webOrder.invoicing.phone : '--' }}
            </span>
          </div>
        </div>
      </div>
    </div>
    }
  </div>
</div>
} @else {
<div class="info-section-card edit-card-wrapper" (click)="$event.stopPropagation()">
  <app-edit-order [order]="order" (edited)="editMode = false"></app-edit-order>
</div>
}